import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { yup } from 'schemas/validation'
import { themeColor, stylesIf } from 'utils/styles'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import useApi from 'hooks/useApi'
import { useDynamicTitle } from 'hooks/useDynamicTitle'
import { useMakeSelector, useSelector } from 'hooks/redux'
import {
  PTO_TEMPLATE,
  PTO_TEMPLATE_COMMIT,
  PTO_TEMPLATE_FRAGMENT,
  DATE_FORMAT_COMMA,
} from 'utils/constants'
import {
  getPtoTemplateById,
  PTO_TEMPLATE_BY_ID_RECEIVED,
} from 'actions/powerTakeOff'
import {
  getFragmentsOutOfRange,
  getTemplateById as getPtoTemplate,
} from 'selectors/powerTakeOff'
import { ORDERED_DOMAIN_IDS } from 'constants/powerTakeOffConstants'
import {
  Row,
  Col,
  Header,
  Loading,
  Table,
  RadioButtonGroup,
  Radio,
  SelectMenu,
  Icon,
  Paragraph,
  Button,
  Tooltip,
} from 'components/patterns'
import { getLocalTime } from 'utils/utils'
import {
  setComponentMeta,
  setSelectedIdWithDrawer,
  setIsEditing,
} from 'actions/navigator.js'
import { getIsEditing, getSelectedId } from 'selectors/navigator'
import strings from 'utils/strings'
import { makeGetIsWaiting } from 'selectors/application'
import { columns } from 'components/vehicles/PowerTakeOff/DomainTable'
import { getDrawerVisibleWidth, getIsDrawerVisible } from 'selectors/drawer'
import { shrinkWhenDrawerOpen } from 'components/patterns/Drawer/Drawer.style'
import { EDIT_PTO_TEMPLATE } from 'constants/authorization'
import { getIsUserInternal, hasRight } from 'selectors/authorization'
import { getIsSameOrgNetwork } from 'selectors/user'
import { setShowDrawerMask } from 'actions/drawer'
import { PageHeader } from '../PtoTemplate.style'

const Template = () => {
  const dispatch = useDispatch()
  const { id: templateId } = useParams()
  const [detailsSelected, updateDetailsSelected] = useState(false)
  const [,] = useApi(getPtoTemplateById, templateId)
  const template = useSelector(getPtoTemplate, { templateId })
  const fragmentsOutOfRange = useSelector(getFragmentsOutOfRange, {
    templateId,
  })
  const drawerVisible = useSelector(getIsDrawerVisible)
  const drawerVisibleWidth = useSelector(getDrawerVisibleWidth)
  const isLoading = useMakeSelector(makeGetIsWaiting, {
    signals: [PTO_TEMPLATE_BY_ID_RECEIVED],
  })
  const { orgId } = template
  const isEditing = useSelector(getIsEditing)
  const isSameOrg = useSelector(getIsSameOrgNetwork, { orgId })
  const isInternal = useSelector(getIsUserInternal)
  const canEdit =
    useSelector(hasRight, EDIT_PTO_TEMPLATE) && (isSameOrg || isInternal)

  const { dirty: isDirty } = template
  const hasInvalidFragments =
    fragmentsOutOfRange && fragmentsOutOfRange.length > 0

  let partNumber = strings.loading
  if (!isLoading && Object.keys(template).length !== 0)
    partNumber = template.partNumber
  useDynamicTitle({ overrideTitle: partNumber })

  useEffect(
    () => () => {
      dispatch(setSelectedIdWithDrawer(null))
      dispatch(setIsEditing(false, PTO_TEMPLATE))
      dispatch(setShowDrawerMask(false))
    },
    // eslint-disable-next-line
    []
  )

  useEffect(() => {
    if (drawerVisible === false && detailsSelected === true) {
      updateDetailsSelected(false)
    }
  }, [drawerVisible, detailsSelected])

  const handleHeaderClick = () => {
    dispatch(
      setComponentMeta(templateId, {
        type: PTO_TEMPLATE,
        template,
      })
    )
    dispatch(setSelectedIdWithDrawer(template.templateId))
    updateDetailsSelected(true)
  }

  const handleEditClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(setIsEditing(!isEditing, PTO_TEMPLATE))
  }

  const handleClickSave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!hasInvalidFragments) {
      dispatch(
        setComponentMeta(templateId, {
          type: PTO_TEMPLATE_COMMIT,
          template,
        })
      )
      dispatch(setSelectedIdWithDrawer(template.templateId))
      dispatch(setShowDrawerMask(true))
    }
  }

  const handleClickCancel = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(setIsEditing(!isEditing, PTO_TEMPLATE))
    if (isDirty) dispatch(getPtoTemplateById(templateId))
  }

  if (isLoading || Object.keys(template).length === 0) return <Loading />

  return (
    <>
      <StyledPageHeader
        onClick={handleHeaderClick}
        selected={detailsSelected}
        drawerVisible={drawerVisible}
        drawerVisibleWidth={drawerVisibleWidth}
        data-testid="page-header"
      >
        <StyledTemplateDetailsContainer selected={detailsSelected}>
          <Row style={{ marginBottom: 10 }}>
            <Col span={24}>
              <Header>{template.name}</Header>
            </Col>
          </Row>
          <Row align="top" style={{ gap: 25 }}>
            <Col key="details" span={7}>
              <StyledTemplateDetails key="number">
                <b>{strings.pto_template_no}:</b> {template.partNumber}
              </StyledTemplateDetails>
              <StyledTemplateDetails key="author">
                <b>{strings.pto_template_author}:</b> {template.author}
              </StyledTemplateDetails>
              <StyledTemplateDetails key="date">
                <b>{strings.pto_template_creation_date}:</b>{' '}
                {getLocalTime(template.timeStamp, DATE_FORMAT_COMMA)}
              </StyledTemplateDetails>
            </Col>
            <Col key="more-details" span={7}>
              <StyledTemplateDetails key="variants">
                <b>{strings.diagnostic_variant}:</b> {template.templateVariant}{' '}
                {template.compatibleVariants &&
                  template.compatibleVariants.length > 1 && (
                    <Tooltip
                      title={strings.pto_template_compatible_app_variants(
                        template.compatibleVariants
                      )}
                      placement="right"
                    >
                      <Icon
                        icon="question_mark_tooltip"
                        fill="outlined"
                        height={14}
                        width={16}
                      />
                    </Tooltip>
                  )}
              </StyledTemplateDetails>
              <StyledTemplateDetails key="engine">
                <b>{strings.pto_template_source_engine}:</b> {template.engine}
              </StyledTemplateDetails>
              <StyledTemplateDetails key="trans">
                <b>{strings.pto_template_source_transmission}:</b>{' '}
                {template.transmission}
              </StyledTemplateDetails>
            </Col>
            <Col key="description" span={7}>
              <StyledDescription>
                <b>{strings.description}:</b>{' '}
                <span>{template.templateDescription}</span>
              </StyledDescription>
            </Col>
          </Row>
        </StyledTemplateDetailsContainer>
        {!isEditing && canEdit && (
          <Button onClick={handleEditClick}>Edit</Button>
        )}
        {isEditing && (
          <>
            <Button type="secondary" onClick={handleClickCancel}>
              {strings.cancel}
            </Button>
            <Button
              type="primary"
              onClick={handleClickSave}
              disabled={!isDirty || hasInvalidFragments}
            >
              {strings.save_changes}
            </Button>
          </>
        )}
      </StyledPageHeader>
      <TemplateDomains {...template} />
    </>
  )
}

function TemplateDomains({ templateId, domains, fragments, ...props }) {
  const dispatch = useDispatch()
  const [filterSettingsToTemplate, updateFilterSettingsToTemplate] =
    useState(true)
  const selectedId = useSelector(getSelectedId)
  const [selectedDomain, updateSelectedDomain] = useState(
    'VCD_PTC_PTO1_Release'
  )

  const noResultText = {
    emptyText: strings.pto_template_has_no_settings_from_domain,
  }

  const filteredFragments = Object.values(fragments)
    .filter((fragment) => {
      if (filterSettingsToTemplate === false) return true
      return fragment.fromDefaultString === false
    })
    .filter((fragment) => fragment.domain === selectedDomain)

  const selectableDomains = ORDERED_DOMAIN_IDS.reduce(
    (sortedDomains, domainId) => {
      if (domains[domainId]) {
        sortedDomains.push({
          key: domains[domainId].id,
          value: domains[domainId].id,
          title: domains[domainId].name,
        })
      }
      return sortedDomains
    },
    []
  )

  const handleFilterSettings = ({ target }) => {
    updateFilterSettingsToTemplate(target.value)
  }

  const handleSelectDomain = (domain) => {
    updateSelectedDomain(domain)
  }

  const handleFragmentSelect = (fragment) => {
    dispatch(
      setComponentMeta(fragment.refNo, {
        type: PTO_TEMPLATE_FRAGMENT,
        templateId,
        refNo: fragment.refNo,
      })
    )
    dispatch(setSelectedIdWithDrawer(fragment.refNo))
  }

  const radioColumn = {
    key: 'radio',
    width: 1,
    render: (_, fragment) => (
      <Radio
        checked={selectedId === fragment.refNo}
        onClick={() => handleFragmentSelect(fragment)}
      />
    ),
  }

  const InfoText = filterSettingsToTemplate
    ? strings.pto_template_filtered_settings_info
    : strings.pto_template_all_settings_info

  return (
    <>
      <div>View:</div>
      <StyledRadioButtonGroup
        currentValue={filterSettingsToTemplate}
        radioGroupClassName="template-domains-radio-btn-group"
        buttons={[
          {
            value: true,
            text: strings.pto_template_domain_settings,
          },
          {
            value: false,
            text: strings.pto_template_all_domain_settings,
          },
        ]}
        onChange={handleFilterSettings}
      />
      <div>
        <Paragraph
          size="small"
          style={{ marginBottom: 16 }}
          color="slatePrimary"
        >
          <Icon
            icon="alert_info_i_round_solid"
            fill="slatePrimary"
            width={12}
            height={12}
          />{' '}
          {InfoText}
        </Paragraph>
      </div>
      <div>
        <div>PTO Domain</div>
        <StyledDomainMenu
          value={selectedDomain}
          onChange={handleSelectDomain}
          rules={yup.string().nullable()}
          options={selectableDomains}
          width={380}
        />
      </div>
      <StyledTable
        columns={[radioColumn, ...columns]}
        dataSource={filteredFragments}
        rowKey={({ refNo }) => refNo}
        rowClassName={({ fromDefaultString }) =>
          fromDefaultString ? 'default' : ''
        }
        onRow={({ refNo }) => ({
          onClick: () => handleFragmentSelect({ refNo }),
        })}
        noResultText={noResultText}
        title=""
        data-testid="template-domains-table"
      />
    </>
  )
}

TemplateDomains.propTypes = {
  templateId: PropTypes.string,
  fragments: PropTypes.object,
  domains: PropTypes.object,
}

export default Template

const StyledPageHeader = styled(PageHeader)(
  (props) => `
  ${shrinkWhenDrawerOpen(props.drawerVisible, props.drawerVisibleWidth)}
  display: flex;
  ${stylesIf(props.drawerVisible, `padding-right: 20px;`)}
  button:first-of-type {
    margin-left: auto;
  }
  `
)

export const StyledTemplateDetails = styled.div`
  --line-height: 18px;
  font-size: 14px;
  line-height: var(--line-height);
  color: ${themeColor('slatePrimary')};

  .tooltip svg {
    vertical-align: top;
  }
`

const StyledDescription = styled(StyledTemplateDetails)`
  --number-of-lines: 6;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: var(--number-of-lines); /* number of lines to show */
  line-clamp: var(--number-of-lines);
  -webkit-box-orient: vertical;
  min-height: calc(var(--line-height) * var(--number-of-lines));
`

const StyledDomainMenu = styled(SelectMenu)(
  (props) => `
    .ant-select {
      width: ${props.width}px;
    }
    margin-bottom: 32px;
  `
)

const StyledTable = styled(Table)(
  (props) => `
    &&&& .ant-table-placeholder {
      height: auto;
      background: white;
      padding: 20px 0;
      color: gray;
    }

    &&&& .ant-table-row.default td,
    && .ant-table-row.default p {
      opacity: 0.4;
    }

    &&&& .ant-table-row:hover td,
    &&&& .ant-table-selected td {
      cursor: pointer;
      background: ${themeColor('blueLight', props)};
    }
  `
)

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  margin-bottom: 5px;
`

const StyledTemplateDetailsContainer = styled.div((props) => {
  const highlightStyles = `background: ${themeColor('blueLight', props)};`
  const selectedHighlight = stylesIf(props.selected, highlightStyles)
  const highlightBar = stylesIf(
    props.selected,
    `
    position: relative;
    &::after{
      content: '';
      display: block;
      position:absolute;
      top:0;
      bottom:0;
      right:0;
      width:6px;
      background: ${themeColor('blueAccent', props)};
    }
    `
  )

  return `
    &:hover {
      ${highlightStyles}
      cursor: pointer;
    }
    padding: 5px 16px 5px 5px;
    margin-right: 16px;
    ${selectedHighlight}
    ${highlightBar}
  `
})
